import { datetimeEn, datetimeIt, en, it, type MessageSchema } from '@/assets/i18n'
import { primevueEn } from '@/assets/i18n/primevue/primevue-en'
import { primevueIt } from '@/assets/i18n/primevue/primevue-it'
import { LocalStorageKey } from '@/stores/stores.type'
import dayjs from 'dayjs'
import type { PrimeVueLocaleOptions } from 'primevue/config'
import { computed } from 'vue'
import { createI18n, useI18n } from 'vue-i18n'

export const locales = ['en-US', 'it-IT'] as const
export type Locale = (typeof locales)[number]

const defaultLocale = 'en-US'
const browserLocale = window.navigator.language as Locale
const storedLocale = localStorage.getItem(LocalStorageKey.Locale) as Locale
const initialLocale = locales.includes(storedLocale)
  ? storedLocale
  : locales.includes(browserLocale)
    ? browserLocale
    : defaultLocale

export const localization = createI18n<[MessageSchema], Locale>({
  locale: initialLocale,
  fallbackLocale: 'en-US',
  legacy: false,
  messages: {
    'it-IT': it,
    'en-US': en,
  },
  datetimeFormats: {
    'it-IT': datetimeIt,
    'en-US': datetimeEn,
  },
  numberFormats: {
    'it-IT': {
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
      },
    },
    'en-US': {
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
      },
    },
  },
})
export const useLocalization = () => {
  const i18n = useI18n<{ message: MessageSchema }>()
  const dateFormats: Record<Locale, string> = {
    'it-IT': 'dd/mm/yy',
    'en-US': 'mm/dd/yy',
  }
  const dateFormatPlaceholders: Record<Locale, string> = {
    'it-IT': 'DD/MM/YYYY',
    'en-US': 'MM/DD/YYYY',
  }
  const { t, locale } = i18n
  const localizeDuration = (durationSeconds: number | undefined | null) => {
    if (durationSeconds == null) {
      return durationSeconds
    }
    const duration = dayjs.duration(durationSeconds, 'seconds')
    const parts: string[] = []
    if (duration.days() > 0) {
      const days = Math.floor(duration.asDays())
      parts.push(days + ' ' + t('duration.days', days))
    }
    const hours = duration.hours()
    if (hours > 0) {
      parts.push(hours + ' ' + t('duration.hours', hours))
    }
    const minutes = duration.minutes()
    if (minutes > 0) {
      parts.push(minutes + ' ' + t('duration.minutes', minutes))
    }
    const seconds = duration.seconds()
    if (seconds > 0) {
      parts.push(seconds + ' ' + t('duration.seconds', seconds))
    }
    return parts.join(', ')
  }

  const primveVueLocales: Record<Locale, PrimeVueLocaleOptions> = {
    'en-US': primevueEn,
    'it-IT': primevueIt,
  }

  const updateLocale = (newLocale: Locale) => {
    locale.value = newLocale as typeof locale.value
    localStorage.setItem(LocalStorageKey.Locale, newLocale)
  }

  return {
    ...i18n,
    dateFormat: computed(() => dateFormats[i18n.locale.value]),
    dateFormatPlaceholders: computed(() => dateFormatPlaceholders[i18n.locale.value]),
    primevueLocale: computed(() => primveVueLocales[i18n.locale.value]),
    dur: localizeDuration,
    updateLocale,
  }
}
